import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  debounce,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  of,
  startWith,
  switchMap,
} from 'rxjs';

import { runtimeSelectors, UrlParametersFacade } from '@blaze-runtime/engine-data-access';
import {
  ConnectorProviderFactory,
  isWorkflowReadyForValidation,
  SchemaProviderFactory,
  type SchemaRegistry,
  type SchemaWithValidationInfo,
  validateWorkflowGraphIntegrity,
  WorkflowError,
  WorkflowNodeFactory,
  type WorkflowValidationContext,
} from '@blaze-runtime/workflow-domain';
import { WorkflowId } from '@blaze-shared/api-contracts-domain';
import { ConnectorsCollectionService } from '@blaze-shared/connectors-data-access';
import { TablesCollectionService } from '@blaze-shared/datasets-data-access';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { loggerCreate } from '@blaze-shared/logger-util';
import { FeatureToggleService } from '@blaze-shared/platform-data-access';
import { DatasetRowsService, SchemaService } from '@blaze-shared/schema-data-access';
import { BaseSchema, BlazeTableSchema, hasBlazeTableFlag } from '@blaze-shared/schema-domain';
import { countedShareReplay, isDeepEqual } from '@blaze-shared/shared-util';
import {
  isWorkflowFunction,
  Workflow,
  WorkflowNode,
  WorkflowPhase,
} from '@blaze-shared/workflow-domain';

interface ProjectMetadata {
  projectId: string;
  pageId: string;
}

/** The schemas each node of one workflow resolves its references against. */
interface SchemasInScope {
  inputSchemas: Map<string | symbol, BaseSchema>;
  outputSchemas: Map<string | symbol, BaseSchema>;
  schemaRegistry: SchemaRegistry;
}

type WorkflowErrorMap = Record<WorkflowId, WorkflowError<any>[]>;

export const logWorkflowValidation = loggerCreate(
  'workflow_validation',
  'info',
  'WorkflowValidationService',
  null,
);

@Injectable({
  providedIn: 'root',
})
export class WorkflowValidationService {
  private readonly store = inject(Store);
  private readonly connectorService = inject(ConnectorsCollectionService);
  private readonly schemaService = inject(SchemaService);
  private readonly intellisenseService = inject(IntelliSenseService);
  private readonly datasetRowsService = inject(DatasetRowsService);
  private readonly urlParametersFacade = inject(UrlParametersFacade);
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly tablesService = inject(TablesCollectionService);

  private readonly workflowErrors$: Observable<WorkflowErrorMap>;

  private readonly userSchema$ = this.store.select(runtimeSelectors.selectProjectId).pipe(
    distinctUntilChanged(),
    switchMap((projectId) => this.fetchAndObserveUserSchema(projectId)),
    countedShareReplay(),
  );

  private readonly accountSchema$ = this.store.select(runtimeSelectors.selectProjectId).pipe(
    distinctUntilChanged(),
    switchMap((projectId) => this.schemaService.obtainAccountsSchema(projectId)),
    countedShareReplay(),
  );

  constructor() {
    this.workflowErrors$ = this.store.select(runtimeSelectors.selectPageId).pipe(
      switchMap(() =>
        this.isAppReady().pipe(
          first(Boolean),
          switchMap(() => this.getProjectMetadata()),
          switchMap((metadata) => this.getWorkflowValidationContext(metadata)),

          debounce(() => this.isDataReady()),
          debounceTime(100),
          map((context) => this.validateWorkflows(context)),
          distinctUntilChanged(isDeepEqual),
        ),
      ),
      countedShareReplay(),
    );
  }

  validateWorkflow(workflow: Workflow): Observable<WorkflowError<any>[]> {
    return combineLatest({
      enabled: combineLatest([
        this.featureToggleService.observeFeatureEnabled('workflow-validation'),
        this.featureToggleService.observeFeatureEnabled('workflow-validation-full'),
      ]).pipe(map(([feature1, feature2]) => feature1 || feature2)),
      context: this.getSingleWorkflowContext(workflow),
    }).pipe(
      debounce(() => this.isDataReady()),
      debounceTime(100),
      map(({ enabled, context }) => {
        if (!enabled) {
          return [];
        }
        return this.validateSingleWorkflow(workflow, context);
      }),
      distinctUntilChanged(isDeepEqual),
      countedShareReplay(),
    );
  }

  selectWorkflowErrors(workflowId: WorkflowId): Observable<WorkflowError<any>[]> {
    return combineLatest({
      enabled: combineLatest([
        this.featureToggleService.observeFeatureEnabled('workflow-validation'),
        this.featureToggleService.observeFeatureEnabled('workflow-validation-full'),
      ]).pipe(map(([feature1, feature2]) => feature1 || feature2)),
      errors: this.workflowErrors$,
    }).pipe(
      map(({ enabled, errors }) => {
        if (!enabled) {
          return [];
        }
        return errors[workflowId] ?? [];
      }),
    );
  }

  selectAllWorkflowsErrors(): Observable<WorkflowErrorMap> {
    return combineLatest({
      enabled: combineLatest([
        this.featureToggleService.observeFeatureEnabled('workflow-validation'),
        this.featureToggleService.observeFeatureEnabled('workflow-validation-full'),
      ]).pipe(map(([feature1, feature2]) => feature1 || feature2)),
      errors: this.workflowErrors$,
    }).pipe(
      map(({ enabled, errors }) => {
        if (!enabled) {
          return {};
        }
        return errors;
      }),
    );
  }

  private getProjectMetadata(): Observable<ProjectMetadata> {
    return combineLatest({
      projectId: this.store.select(runtimeSelectors.selectProjectId),
      pageId: this.store.select(runtimeSelectors.selectPageId),
    });
  }

  private getWorkflowValidationContext(
    metadata: ProjectMetadata,
  ): Observable<WorkflowValidationContext> {
    return combineLatest({
      workflows: combineLatest([
        this.store.select(runtimeSelectors.selectWorkflowsByPageId(metadata.pageId)),
        this.selectWorkflowFunctions(),
      ]).pipe(map(([pageWorkflows, functions]) => [...pageWorkflows, ...functions])),
      connections: this.store.select(runtimeSelectors.selectConnections),
      schemaRegistry: this.getSchemaRegistry(metadata.pageId),
      connectors: this.connectorService.entities$,
      tables: this.tablesService.entities$,
    });
  }

  private getSingleWorkflowContext(workflow: Workflow): Observable<WorkflowValidationContext> {
    return combineLatest({
      workflows: this.selectWorkflowFunctions().pipe(map((functions) => [workflow, ...functions])),
      connections: this.store.select(runtimeSelectors.selectConnections),
      schemaRegistry: this.getSchemaRegistry(workflow.pageId),
      connectors: this.connectorService.entities$,
      tables: this.tablesService.entities$,
    });
  }

  private selectWorkflowFunctions(): Observable<Workflow[]> {
    return this.store
      .select(runtimeSelectors.selectWorkflows)
      .pipe(map((workflows) => workflows.filter(isWorkflowFunction)));
  }

  private getSchemaRegistry(pageId: string | undefined | null): Observable<SchemaRegistry> {
    return combineLatest({
      tableSchemas: this.store.select(runtimeSelectors.selectSchemas),
      userSchema: this.userSchema$,
      urlSchema: this.urlParametersFacade.getUrlSchema$(pageId),
      accountSchema: this.accountSchema$,
    });
  }

  private fetchAndObserveUserSchema(projectId: string): Observable<BlazeTableSchema | null> {
    return this.schemaService.obtainUsersSchema(projectId).pipe(
      switchMap((schema) => {
        if (!schema?.id) {
          console.warn(`User schema for project ${projectId} not found or invalid.`);
          return of(null);
        }

        return this.datasetRowsService.observeDatasetChanged(schema.id).pipe(
          switchMap(() => this.schemaService.obtainUsersSchema(projectId)),
          startWith(schema),
        );
      }),
    );
  }

  private validateWorkflows(context: WorkflowValidationContext): WorkflowErrorMap {
    // Functions are in context.workflows for lookup only; page validation stays page-scoped.
    return context.workflows
      .filter((workflow) => !isWorkflowFunction(workflow))
      .reduce(
        (errorMap, workflow) => ({
          ...errorMap,
          [workflow.id]: this.validateSingleWorkflow(workflow, context),
        }),
        {} as WorkflowErrorMap,
      );
  }

  private validateSingleWorkflow(
    workflow: Workflow,
    context: WorkflowValidationContext,
  ): WorkflowError<any>[] {
    const isFullValidationTurnedOn = this.featureToggleService.isFeatureEnabled(
      'workflow-validation-full',
    );
    if (!isFullValidationTurnedOn && !isWorkflowReadyForValidation(workflow, context)) {
      logWorkflowValidation('validateSingleWorkflow', (tag) =>
        console.log(
          `${tag} 🚫 Skipping validation for workflow ${workflow.id} as some nodes are not ready for validation`,
        ),
      );
      return [];
    }

    const workflowIntegrityErrors = validateWorkflowGraphIntegrity(workflow);
    if (workflowIntegrityErrors.length > 0) {
      return workflowIntegrityErrors;
    }

    const schemasInScope = this.collectSchemasInScope(workflow, context);

    return workflow.nodes.flatMap((node) => {
      return this.validateNode(node, workflow, schemasInScope, context);
    });
  }

  private collectSchemasInScope(
    workflow: Workflow,
    context: WorkflowValidationContext,
  ): SchemasInScope {
    const { nodeInputSchemasMap, nodeOutputSchemasMap } =
      this.intellisenseService.calculateSchemaForWorkflow(workflow, null, null);

    const newNodeInputSchemasMap = new Map<string | symbol, BaseSchema>();
    const newNodeOutputSchemasMap = new Map<string, BaseSchema>();

    nodeInputSchemasMap.forEach((schema, key) => {
      newNodeInputSchemasMap.set(key, this.addSchemaValidationInfo(schema, context));
    });
    nodeOutputSchemasMap.forEach((schema, key) => {
      newNodeOutputSchemasMap.set(key, this.addSchemaValidationInfo(schema, context));
    });
    const newTableSchemas = Object.fromEntries(
      Object.entries(context.schemaRegistry.tableSchemas).map(([key, schema]) => {
        return [key, this.addSchemaValidationInfo(schema, context)];
      }),
    );
    const { userSchema, accountSchema, urlSchema } = context.schemaRegistry;
    const newSchemaRegistry = {
      ...context.schemaRegistry,
      tableSchemas: newTableSchemas,
      userSchema: userSchema ? this.addSchemaValidationInfo(userSchema, context) : null,
      accountSchema: accountSchema ? this.addSchemaValidationInfo(accountSchema, context) : null,
      urlSchema: urlSchema,
    };

    return {
      inputSchemas: newNodeInputSchemasMap,
      outputSchemas: newNodeOutputSchemasMap,
      schemaRegistry: newSchemaRegistry,
    };
  }

  private addSchemaValidationInfo<T extends BaseSchema>(
    schema: T,
    context: WorkflowValidationContext,
  ): T & SchemaWithValidationInfo {
    const availableInCurrentProject = hasBlazeTableFlag(schema)
      ? context.tables.some((t) => t.id === schema.id)
      : true;

    return {
      ...schema,
      validationInfo: {
        availableInCurrentProject,
      },
    };
  }

  private validateNode(
    node: WorkflowNode,
    workflow: Workflow,
    schemasInScope: SchemasInScope,
    context: WorkflowValidationContext,
  ): WorkflowError<any>[] {
    const workflowNode = WorkflowNodeFactory.createNode(node, workflow.id);
    const schemaProvider = SchemaProviderFactory.createSchemaProvider(node);
    const connectorProvider = ConnectorProviderFactory.createConnectorProvider(node);

    const schemaContext = {
      input: schemasInScope.inputSchemas,
      output: schemasInScope.outputSchemas,
      allSchemas: schemasInScope.schemaRegistry.tableSchemas,
      userSchema: schemasInScope.schemaRegistry.userSchema,
      accountSchema: schemasInScope.schemaRegistry.accountSchema,
      urlSchema: schemasInScope.schemaRegistry.urlSchema,
    };

    const nodeSchemas = schemaProvider.getSchemas(
      workflow,
      node,
      schemaContext,
      context.connections,
    );

    const validationResult: WorkflowError<any>[] = workflowNode.validate(
      nodeSchemas,
      connectorProvider.getConnectors(workflow, node, context.connectors),
      context.workflows,
    );

    logWorkflowValidation('validateNode', (tag) =>
      console.log(
        `${tag} 🦹 Validating node ${node.id} of type ${node.nodeType} in workflow ${workflow.id}`,
        workflow.name ? `(name: ${workflow.name})` : '',
        '\n',
        {
          node,
          workflow,
          nodeSchemas,
          schemaContext,
          schemasInScope,
          context,
          validationResult,
        },
      ),
    );

    return validationResult;
  }

  private isAppReady(): Observable<boolean> {
    return combineLatest([
      this.store.select(runtimeSelectors.selectWorkflowsStatus).pipe(
        map((workflowsStatus) =>
          Object.values(workflowsStatus).some((status) => status?.phase === WorkflowPhase.Running),
        ),
        map((hasWorkflowsRunning) => !hasWorkflowsRunning),
      ),
      this.store
        .select(runtimeSelectors.selectPendingPaginations)
        .pipe(map((paginations) => Object.keys(paginations).length === 0)),
      this.store.select(runtimeSelectors.selectIsRuntimeInfoFetched),
      this.store.select(runtimeSelectors.selectIsRunning),
      this.store
        .select(runtimeSelectors.selectPendingUi)
        .pipe(map((pendingUi) => Object.keys(pendingUi).length === 0)),
      this.store
        .select(runtimeSelectors.selectConnectionChains)
        .pipe(map((chains) => Object.values(chains).flat().length === 0)),
    ]).pipe(
      map((conditions) => conditions.every(Boolean)),
      debounceTime(1000),
    );
  }

  private isDataReady(): Observable<void> {
    return this.tablesService.loaded$.pipe(
      filter(Boolean),
      map(() => undefined),
    );
  }
}
